import { useState, useEffect } from 'react';
import { url } from '../utils/config';
import Table from '../reusable_components/Table';
import axios from 'axios';
import { btnOne, btnSmall, btnSmallTwo, btnWhite, modalContainer, modalTitle, title } from '../utils/style';
import uiConfigAtom from '../atoms/uiConfigAtom';
import { useRecoilState } from 'recoil';
import formatNumberToUs from '../custom_functions/formatNumberToUs';
import { FiCalendar, FiEdit, FiCheck, FiChevronDown, FiChevronUp, FiCopy } from 'react-icons/fi';
import Button from '../reusable_components/Button';
import patchData from '../custom_functions/patchData';
import { toast } from 'react-toastify';


function FcQuote() {
    const [viewData, setViewData] = useState(null);
    const [reloadTable, setReloadTable] = useState(false);
    const [workOrder, setWorkOrder] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [estimatedDate, setEstimatedDate] = useState('');
    const [customList, setCustomList] = useState([]);
    const [tireList, setTireList] = useState([]);
    const [bundleList, setBundleList] = useState([]);
    const [editingCustomNameIndex, setEditingCustomNameIndex] = useState(null);
    const [isNotesOpen, setIsNotesOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (viewData) {
            handleEyeClick(viewData);
        }
    }, [viewData]);

    const options = [
        {
            header: 'Customer',
            render: (data) => {
                return data.fleet_customer.name
            },
            filterKey: 'fleet_customer_name'
        },
        {
            header: 'Make',
            render: (data) => {
                return data.fleet_customer_car.make
            },
            filterKey: 'fleet_customer_car.make'
        },
        {
            header: 'Model',
            render: (data) => {
                return data.fleet_customer_car.model
            },
            filterKey: 'fleet_customer_car.model'
        },
        {
            header: 'Year',
            render: (data) => {
                return data.fleet_customer_car.year
            },
            filterKey: 'fleet_customer_car.year'
        },
        {
            header: 'Total Cost',
            render: (data) => {
                return `$${formatNumberToUs(data.totalCost)}`
            },
            filterKey: 'totalCost'
        },
        {
            header: 'Approval',
            render: (data) => {
                const isAutoApproved = data.bypassApproval === 'yes';
                return (
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${isAutoApproved
                        ? 'bg-green-100 text-green-800'
                        : 'bg-orange-100 text-orange-800'
                        }`}>
                        {isAutoApproved ? 'Auto Approved' : 'Approval Required'}
                    </span>
                );
            },
        },
        {
            header: 'Status',
            render: (data) => {
                const statusColors = {
                    'Pending': 'bg-yellow-100 text-yellow-800',
                    'Modified': 'bg-blue-100 text-blue-800',
                    'Approved': 'bg-green-100 text-green-800',
                    'Rejected': 'bg-red-100 text-red-800',
                    'Invoiced': 'bg-purple-100 text-purple-800',
                    'Paid': 'bg-emerald-100 text-emerald-800',
                    'Failed': 'bg-red-100 text-red-800',
                    'InService': 'bg-indigo-100 text-indigo-800',
                    'Completed': 'bg-teal-100 text-teal-800'
                };
                const colorClass = statusColors[data.status] || 'bg-gray-100 text-gray-800';
                return (
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${colorClass}`}>
                        {data.status}
                    </span>
                );
            },
            filterKey: 'status'
        },
        {
            header: 'InService',
            render: (data) => {
                const isDisabled = data.status === 'Failed' || data.status === 'Rejected' || data.status === 'Paid' || data.status === 'InService' || data.status === 'Completed' || (data.bypassApproval === 'no' && data.status !== 'Approved');
                return (
                    <button
                        onClick={(e) => handleStatusUpdate(data.id, 'InService', data.statusTracking, e)}
                        disabled={isDisabled}
                        className={`px-4 py-1 rounded-full text-xs font-medium transition-all duration-200 ${isDisabled
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-indigo-500 text-white hover:bg-indigo-600 hover:shadow-md'
                            }`}
                    >
                        Start Service
                    </button>
                );
            }
        },
        {
            header: 'Complete',
            render: (data) => {
                const isDisabled = data.status !== 'InService';
                return (
                    <button
                        onClick={(e) => handleStatusUpdate(data.id, 'Completed', data.statusTracking, e, data)}
                        disabled={isDisabled}
                        className={`px-4 py-1 rounded-full text-xs font-medium transition-all duration-200 ${isDisabled
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-teal-500 text-white hover:bg-teal-600 hover:shadow-md'
                            }`}
                    >
                        Complete Service
                    </button>
                );
            }
        },
        {
            header: 'Created At',
            render: data => {
                const date = new Date(data.createdAt);
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const year = date.getFullYear();
                return `${month}-${day}-${year}`;
            },
        }

    ]


    const handleStatusUpdate = async (id, status, statusTracking, e, workOrderData = null) => {
        e.stopPropagation();

        // If completing service, validate all required fields
        if (status === 'Completed' && workOrderData) {
            // Validate estimated date
            if (!workOrderData.estimatedDate) {
                toast.error('Please set an estimated completion date', { autoClose: 4000 });
                return;
            }

            // Validate custom list items have prices and names
            if (workOrderData.customList && workOrderData.customList.length > 0) {
                const missingPrices = workOrderData.customList.some(item =>
                    item.cost === null || item.cost === undefined || item.cost === '' || parseFloat(item.cost) <= 0
                );
                if (missingPrices) {
                    toast.error('All custom services must have valid prices before completing', { autoClose: 4000 });
                    return;
                }

                const missingNames = workOrderData.customList.some(item =>
                    !item.name && !item.itemName
                );
                if (missingNames) {
                    toast.error('All custom services must have names before completing', { autoClose: 4000 });
                    return;
                }
            }

            // Validate tire list items have prices
            if (workOrderData.tireList && workOrderData.tireList.length > 0) {
                const missingTirePrices = workOrderData.tireList.some(item =>
                    !item.cost || parseFloat(item.cost) <= 0
                );
                if (missingTirePrices) {
                    toast.error('All tires must have valid prices before completing', { autoClose: 4000 });
                    return;
                }
            }

            // Validate tire bundle details
            if (workOrderData.bundleList && workOrderData.bundleList.length > 0) {
                const tireBundles = workOrderData.bundleList.filter(bundle =>
                    /tire.*mount.*and.*balance/i.test(bundle?.name || "")
                );

                for (const bundle of tireBundles) {
                    // Check if tire details exist
                    const hasTireInfo = bundle?.tireType || bundle?.tireQuantity || bundle?.tireNotes;

                    if (hasTireInfo) {
                        // Validate all tire bundle required fields
                        if (!bundle.tireQuantity || bundle.tireQuantity <= 0) {
                            toast.error('Please set tire quantity for all tires', { autoClose: 4000 });
                            return;
                        }
                        if (!bundle.tireBrand || bundle.tireBrand.trim() === '') {
                            toast.error('Please enter tire brand for all tires', { autoClose: 4000 });
                            return;
                        }
                        if (!bundle.tireCost || parseFloat(bundle.tireCost) <= 0) {
                            toast.error('Please enter tire price for all tires', { autoClose: 4000 });
                            return;
                        }
                    }
                }
            }
        }

        try {
            const payload = {
                status: status,
                statusTracking: statusTracking || [],
                fleet_customer_id: workOrderData?.fleet_customer_id || null
            };
            await patchData(`${url}/fc_service_req_status/${id}`, payload,
                (error) => {
                    toast.error(error?.response?.data?.message || error.message, { autoClose: 3000 })
                },
                (data) => {
                    toast.success(data.message, { autoClose: 3000 });
                    setReloadTable(!reloadTable);
                }
            );
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error('Failed to update status', { autoClose: 3000 });
        }
    };

    const handleEyeClick = (data) => {
        setShowModal(true);
        setWorkOrder(data);
        setEstimatedDate(data?.estimatedDate ? data.estimatedDate.split('T')[0] : '');
        setCustomList(data?.customList || []);
        setTireList(data?.tireList || []);
        setBundleList(data?.bundleList || []);
        setEditingCustomNameIndex(null);
    };

    const isTireBundle = (bundle) => {
        return /tire.*mount.*and.*balance/i.test(bundle?.name || "");
    };

    const handleBundleTireCostChange = (index, value) => {
        const sanitizedValue = value.replace(/[^0-9.]/g, '');
        const updated = [...bundleList];
        updated[index] = { ...updated[index], tireCost: sanitizedValue === '' ? null : parseFloat(sanitizedValue) };
        setBundleList(updated);
    };

    const handleBundleMetadataChange = (index, field, value) => {
        const updated = [...bundleList];
        if (field === 'tireQuantity') {
            const sanitizedValue = String(value).replace(/[^0-9]/g, '');
            updated[index] = { ...updated[index], [field]: sanitizedValue === '' ? null : parseInt(sanitizedValue) };
        } else {
            updated[index] = { ...updated[index], [field]: value };
        }
        setBundleList(updated);
    };

    const hasTireDetails = (bundle) => {
        return bundle?.tireType || bundle?.tireQuantity || bundle?.tireNotes;
    };

    const handleCustomPriceChange = (index, value) => {
        const sanitizedValue = value.replace(/[^0-9.]/g, '');
        const updated = [...customList];
        updated[index] = { ...updated[index], cost: sanitizedValue === '' ? null : parseFloat(sanitizedValue) };
        setCustomList(updated);
    };

    const handleCustomNameChange = (index, value) => {
        const updated = [...customList];
        updated[index] = { ...updated[index], name: value };
        setCustomList(updated);
    };

    const handleTireQuantityChange = (index, value) => {
        const sanitizedValue = value.replace(/[^0-9]/g, '');
        const updated = [...tireList];
        const quantity = parseInt(sanitizedValue) || 0;
        const tire = updated[index];
        const updatedTire = { ...tire, quantity };

        if (tire.includeTax && tire.cost) {
            const itemTotal = (parseFloat(tire.cost) || 0) * quantity;
            const taxAmount = itemTotal * ((parseFloat(workOrder?.taxRatePercent) || 0) / 100);
            updatedTire.taxAmount = parseFloat(taxAmount.toFixed(2));
        }

        updated[index] = updatedTire;
        setTireList(updated);
    };

    const handleTireNotesChange = (index, value) => {
        const updated = [...tireList];
        updated[index] = { ...updated[index], notes: value };
        setTireList(updated);
    };

    const handleTirePriceChange = (index, value) => {
        const sanitizedValue = value.replace(/[^0-9.]/g, '');
        const updated = [...tireList];
        const cost = sanitizedValue === '' ? null : parseFloat(sanitizedValue);
        const tire = updated[index];
        const updatedTire = { ...tire, cost };

        if (tire.includeTax && cost) {
            const itemTotal = cost * (parseFloat(tire.quantity) || 1);
            const taxAmount = itemTotal * ((parseFloat(workOrder?.taxRatePercent) || 0) / 100);
            updatedTire.taxAmount = parseFloat(taxAmount.toFixed(2));
        } else if (!cost) {
            updatedTire.taxAmount = 0;
        }

        updated[index] = updatedTire;
        setTireList(updated);
    };

    const handleTireTaxToggle = (index, checked) => {
        const updated = [...tireList];
        const tire = updated[index];
        const updatedTire = { ...tire, includeTax: checked };

        if (checked && tire.cost) {
            const itemTotal = (parseFloat(tire.cost) || 0) * (parseFloat(tire.quantity) || 1);
            const taxAmount = itemTotal * ((parseFloat(workOrder?.taxRatePercent) || 0) / 100);
            updatedTire.taxAmount = parseFloat(taxAmount.toFixed(2));
        } else {
            updatedTire.taxAmount = 0;
        }

        updated[index] = updatedTire;
        setTireList(updated);
    };

    const serviceTotal = workOrder?.serviceList?.reduce((sum, item) => sum + (parseFloat(item?.price || item?.cost) || 0), 0) || 0;
    const customTotal = customList?.reduce((sum, item) => sum + (parseFloat(item?.price || item?.cost) || 0), 0) || 0;
    const bundleTotal = bundleList?.reduce((sum, item) => {
        const baseCost = parseFloat(item?.cost) || 0;
        const tireCost = isTireBundle(item) ? ((parseFloat(item?.tireCost) || 0) * (parseFloat(item?.tireQuantity) || 1)) : 0;
        return sum + baseCost + tireCost;
    }, 0) || 0;
    const materialTotal = workOrder?.materialList?.reduce((sum, item) => sum + ((parseFloat(item?.cost) || 0) + (parseFloat(item?.labour) || 0)), 0) || 0;
    const tireTotal = tireList?.reduce((sum, item) => sum + ((parseFloat(item?.cost) || 0) * (parseFloat(item?.quantity) || 1)), 0) || 0;
    const tireTaxTotal = tireList?.reduce((sum, item) => {
        if (item?.includeTax) {
            const itemTotal = (parseFloat(item?.cost) || 0) * (parseFloat(item?.quantity) || 1);
            return sum + (itemTotal * ((parseFloat(workOrder?.taxRatePercent) || 0) / 100));
        }
        return sum;
    }, 0) || 0;
    // Only add tire tax if status is Pending or Rejected (not yet processed)
    // For Modified/Approved/etc status, workOrder.tax already includes tire tax
    const shouldAddTireTax = workOrder?.status === 'Pending' || workOrder?.status === 'Rejected';
    const calculatedTax = (parseFloat(workOrder?.tax) || 0) + (shouldAddTireTax ? tireTaxTotal : 0);
    const calculatedSubtotal = serviceTotal + customTotal + bundleTotal + materialTotal + tireTotal;
    const calculatedTotal = calculatedSubtotal + calculatedTax;

    const handleSubmit = async () => {
        // Check if form is editable based on bypassApproval and status
        const isFormEditable = workOrder?.bypassApproval === 'no'
            ? (workOrder?.status === 'Pending' || workOrder?.status === 'Rejected')
            : (workOrder?.status !== 'Completed' && workOrder?.status !== 'Invoiced' && workOrder?.status !== 'Paid' && workOrder?.status !== 'Failed');

        if (!isFormEditable) {
            return;
        }

        // Validation only when bypassApproval is "no"
        if (workOrder?.bypassApproval === 'no') {
            if (!estimatedDate) {
                toast.error('Please select an estimated completion date', { autoClose: 3000 });
                return;
            }

            if (customList?.length > 0) {
                const missingPrices = customList.some(item => item.cost === null || item.cost === undefined || item.cost === '' || parseFloat(item.cost) <= 0);
                if (missingPrices) {
                    toast.error('Please enter a valid price for all custom services', { autoClose: 3000 });
                    return;
                }
                const missingNames = customList.some(item => !item.name && !item.itemName);
                if (missingNames) {
                    toast.error('Please enter name for all custom services', { autoClose: 3000 });
                    return;
                }
            }

            if (tireList?.length > 0) {
                const missingTirePrices = tireList.some(item => !item.cost || parseFloat(item.cost) <= 0);
                if (missingTirePrices) {
                    toast.error('Please enter price for all tires', { autoClose: 3000 });
                    return;
                }
            }

            // Validate tire bundle details
            if (bundleList?.length > 0) {
                const tireBundles = bundleList.filter(bundle =>
                    /tire.*mount.*and.*balance/i.test(bundle?.name || "")
                );

                for (const bundle of tireBundles) {
                    // Check if tire details exist
                    const hasTireInfo = bundle?.tireType || bundle?.tireQuantity || bundle?.tireNotes;

                    if (hasTireInfo) {
                        // Validate all tire bundle required fields
                        if (!bundle.tireQuantity || bundle.tireQuantity <= 0) {
                            toast.error('Please set tire quantity for all tires', { autoClose: 3000 });
                            return;
                        }
                        if (!bundle.tireBrand || bundle.tireBrand.trim() === '') {
                            toast.error('Please enter tire brand for all tires', { autoClose: 3000 });
                            return;
                        }
                        if (!bundle.tireCost || parseFloat(bundle.tireCost) <= 0) {
                            toast.error('Please enter tire price for all tires', { autoClose: 3000 });
                            return;
                        }
                    }
                }
            }
        }

        try {
            setIsSubmitting(true);
            // When saving, always calculate the full tax (base tax + tire tax)
            const baseTax = parseFloat(workOrder?.tax) || 0;
            const fullTax = shouldAddTireTax ? (baseTax + tireTaxTotal) : baseTax;
            const fullTotal = calculatedSubtotal + fullTax;
            const payload = {
                customList: customList,
                tireList: tireList,
                bundleList: bundleList,
                estimatedDate: estimatedDate || null,
                totalCost: fullTotal.toFixed(2),
                tax: fullTax.toFixed(2),
                bypassApproval: workOrder?.bypassApproval,
                status: workOrder?.status,
                statusTracking: workOrder?.statusTracking
            };
            patchData(`${url}/fc_service_req/${workOrder.id}`, payload,
                (error) => {
                    toast.error(error?.response?.data?.message || error.message, { autoClose: 3000 })
                },
                (data) => {
                    toast.success(data.message, { autoClose: 3000 });
                    setShowModal(false);
                    setViewData(null);
                    setEditingCustomNameIndex(null);
                    setReloadTable(!reloadTable);
                }
            );


        } catch (error) {
            console.error('Error updating quote:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const excludeFilter = ['Total Cost', 'Created At', 'InService', 'Complete', 'Approval'];
    const isEditable = workOrder?.bypassApproval === 'no'
        ? (workOrder?.status === 'Pending' || workOrder?.status === 'Rejected')
        : (workOrder?.status !== 'Completed' && workOrder?.status !== 'Invoiced' && workOrder?.status !== 'Paid' && workOrder?.status !== 'Failed');
    const customerName = workOrder?.fleet_customer?.name || '—';
    const vehicleDetails = workOrder?.fleet_customer_car
        ? [
            workOrder.fleet_customer_car.make,
            workOrder.fleet_customer_car.model,
            workOrder.fleet_customer_car.year
        ].filter(Boolean).join(' ')
        : '—';

    return (
        <div className="p-4">
            <div className={`${title} mb-4`}>Fleet Service Request</div>
            {showModal && (
                <div className={modalContainer}>
                    <div className='bg-white rounded-2xl p-6 w-full max-w-4xl shadow-2xl border border-gray-100 max-h-[90vh] flex flex-col'>
                        <div className='flex justify-between items-center mb-6'>
                            <div className='text-xl font-semibold text-gray-800'>Quote Overview</div>
                            <div className='flex gap-3'>
                                <Button
                                    clickHandler={(e) => { setShowModal(false); setViewData(null); setEditingCustomNameIndex(null); }}
                                    className={`${btnOne}`}
                                    name={"Cancel"}
                                />
                                <Button
                                    clickHandler={handleSubmit}
                                    className={`${btnOne} ${!isEditable ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    name={isSubmitting ? "Submitting..." : "Submit"}
                                    disabled={isSubmitting || !isEditable}
                                />
                            </div>
                        </div>

                        <div className='px-2 sm:px-4 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-blue-200 flex-1'>
                            <div className='bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-xl px-6 py-5 mb-6 grid gap-6 lg:grid-cols-[1fr_1fr_auto] shadow-sm'>
                                <div>
                                    <div className='text-xs font-medium text-blue-600 mb-1'>Customer</div>
                                    <div className='text-sm text-gray-800'>{customerName}</div>
                                    <div className='text-xs font-medium text-blue-600 mb-1 mt-2'>VIN</div>
                                    <div className='text-sm text-gray-800 flex items-center gap-2'>
                                        <span>{workOrder?.fleet_customer_car?.vin || '—'}</span>
                                        {workOrder?.fleet_customer_car?.vin && (
                                            <button
                                                onClick={() => {
                                                    navigator.clipboard.writeText(workOrder?.fleet_customer_car?.vin);
                                                    toast.success('VIN copied to clipboard', { autoClose: 3000 });
                                                }}
                                                className='text-blue-500 hover:text-blue-700 transition-colors'
                                                title='Copy VIN'
                                            >
                                                <FiCopy className='text-sm' />
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <div className='text-xs font-medium text-blue-600 mb-1'>Vehicle</div>
                                    <div className='text-sm text-gray-800'>{vehicleDetails}</div>
                                </div>
                                <div className=' items-stretch justify-start lg:justify-end'>
                                    {/* <div className='flex flex-col gap-3 bg-white bg-opacity-70 border border-blue-200 rounded-xl px-4 py-4 shadow-sm w-full lg:w-auto'> */}
                                    <div className='flex items-center gap-2 text-blue-600 font-medium text-xs mb-1'>
                                        <FiCalendar className='text-lg' />
                                        <span>Estimated Completion Date</span>
                                    </div>
                                    <input
                                        type='date'
                                        min={new Date().toISOString().split('T')[0]}
                                        value={estimatedDate || ''}
                                        onChange={(e) => setEstimatedDate(e.target.value)}
                                        disabled={!isEditable}
                                        className={`border border-blue-200 bg-white rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-blue-400 flex-1 min-w-[120px] shadow-inner ${!isEditable ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
                                    />
                                    {/* </div> */}
                                </div>
                            </div>

                            {workOrder?.serviceList?.length ? (
                                <div className='border border-gray-200 rounded-xl overflow-hidden shadow-md bg-white mb-6'>
                                    <div className='flex justify-between items-center px-5 py-3 bg-gray-50 text-xs font-medium text-gray-600'>
                                        <span>Service</span>
                                        <span>Price</span>
                                    </div>
                                    {workOrder.serviceList?.map((service, index) => (
                                        <div
                                            key={`service-${index}`}
                                            className={`flex justify-between items-center px-5 py-3 border-b border-gray-200 last:border-b-0 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-blue-50 transition-colors`}
                                        >
                                            <span className='text-gray-700'>{service?.itemName || service?.name || 'Service'}</span>
                                            <span className='text-gray-800 font-medium'>${formatNumberToUs(service?.price || service?.cost || 0)}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : null}

                            {bundleList?.length ? (
                                <div className='border border-gray-200 rounded-xl overflow-hidden shadow-md bg-white mb-6'>
                                    <div className='flex justify-between items-center px-5 py-3 bg-green-50 text-xs font-medium text-green-700'>
                                        <span>Bundle</span>
                                        <span>Price</span>
                                    </div>
                                    {bundleList.map((bundle, index) => {
                                        const isTireBundleItem = isTireBundle(bundle);
                                        const hasTireInfo = hasTireDetails(bundle);
                                        return (
                                            <div
                                                key={`bundle-${index}`}
                                                className={`px-5 py-3 border-b border-gray-200 last:border-b-0 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-green-50 transition-colors`}
                                            >
                                                <div className='flex justify-between items-center'>
                                                    <span className='text-gray-700'>{bundle?.name || 'Bundle'}</span>
                                                    <span className='text-gray-800 font-medium'>${formatNumberToUs(bundle?.cost ?? 0)}</span>
                                                </div>
                                                {isTireBundleItem && !hasTireInfo && isEditable && (
                                                    <div className='mt-3'>
                                                        <button
                                                            onClick={() => handleBundleMetadataChange(index, 'tireQuantity', 1)}
                                                            className='flex items-center gap-2 px-3 py-2 bg-green-100 text-green-700 rounded-lg text-sm font-medium hover:bg-green-200 transition-colors'
                                                        >
                                                            <span>+ Add Tire Details</span>
                                                        </button>
                                                    </div>
                                                )}
                                                {isTireBundleItem && (hasTireInfo || !isEditable) && (
                                                    <div className='mt-3 p-3 bg-green-50 rounded-lg border border-green-200'>
                                                        <div className='grid grid-cols-2 gap-4 mb-2'>
                                                            <div>
                                                                <span className='text-xs text-green-700 font-medium'>Tire Type</span>
                                                                {isEditable ? (
                                                                    <select
                                                                        value={bundle?.tireType || ""}
                                                                        onChange={(e) => handleBundleMetadataChange(index, 'tireType', e.target.value)}
                                                                        className='w-full mt-1 rounded-lg border border-green-300 px-2 py-1 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-400 bg-white'
                                                                    >
                                                                        <option value="All-Season">All-Season</option>
                                                                        <option value="Winter">Winter</option>
                                                                        <option value="Summer">Summer</option>
                                                                        <option value="All-Terrain">All-Terrain</option>
                                                                        <option value="Performance">Performance</option>
                                                                        <option value="Run-Flats">Run-Flats</option>
                                                                        <option value="Temporary Spares">Temporary Spares</option>
                                                                        <option value="Trailer Tires">Trailer Tires</option>
                                                                    </select>
                                                                ) : (
                                                                    <p className='text-sm text-gray-700'>{bundle?.tireType || '—'}</p>
                                                                )}
                                                            </div>
                                                            <div>
                                                                <span className='text-xs text-green-700 font-medium'>Quantity</span>
                                                                {isEditable ? (
                                                                    <select
                                                                        value={bundle?.tireQuantity || 1}
                                                                        onChange={(e) => handleBundleMetadataChange(index, 'tireQuantity', e.target.value)}
                                                                        className='w-full mt-1 rounded-lg border border-green-300 px-2 py-1 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-400 bg-white'
                                                                    >
                                                                        <option value="1">1</option>
                                                                        <option value="2">2</option>
                                                                        <option value="3">3</option>
                                                                        <option value="4">4</option>
                                                                    </select>
                                                                ) : (
                                                                    <p className='text-sm text-gray-700'>{bundle?.tireQuantity || 1}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className='grid grid-cols-2 gap-4 mb-2'>
                                                            <div>
                                                                <span className='text-xs text-green-700 font-medium'>Tire Brand</span>
                                                                {isEditable ? (
                                                                    <input
                                                                        type='text'
                                                                        placeholder='Enter tire brand...'
                                                                        value={bundle?.tireBrand || ''}
                                                                        onChange={(e) => handleBundleMetadataChange(index, 'tireBrand', e.target.value)}
                                                                        className='w-full mt-1 border border-green-300 rounded-lg px-2 py-1 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-400'
                                                                    />
                                                                ) : (
                                                                    <p className='text-sm text-gray-700'>{bundle?.tireBrand || '—'}</p>
                                                                )}
                                                            </div>
                                                            <div>
                                                                <span className='text-xs text-green-700 font-medium'>Tire Change Reason</span>
                                                                <input
                                                                    type='text'
                                                                    value={bundle?.tireChangeReason?.replace(/_/g, ' ') || ''}
                                                                    readOnly
                                                                    className='w-full mt-1 border border-green-300 rounded-lg px-2 py-1 text-sm text-gray-700 bg-gray-100 cursor-not-allowed'
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className='grid grid-cols-2 gap-4 mb-2'>
                                                            <div>
                                                                <span className='text-xs text-green-700 font-medium'>Tire Size</span>
                                                                {isEditable ? (
                                                                    <input
                                                                        type='text'
                                                                        placeholder='Enter tire size...'
                                                                        value={bundle?.tireSize || ''}
                                                                        onChange={(e) => handleBundleMetadataChange(index, 'tireSize', e.target.value)}
                                                                        className='w-full mt-1 border border-green-300 rounded-lg px-2 py-1 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-400'
                                                                    />
                                                                ) : (
                                                                    <p className='text-sm text-gray-700 mt-1'>{bundle?.tireSize || '—'}</p>
                                                                )}
                                                            </div>
                                                            <div>
                                                                <span className='text-xs text-green-700 font-medium'>Price Per Tire ($)</span>
                                                                <div className='flex items-center gap-1 mt-1'>
                                                                    <input
                                                                        type='number'
                                                                        step='0.01'
                                                                        min='0'
                                                                        placeholder='0.00'
                                                                        value={bundle?.tireCost ?? ''}
                                                                        onChange={(e) => handleBundleTireCostChange(index, e.target.value)}
                                                                        onKeyDown={(e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
                                                                        disabled={!isEditable}
                                                                        className={`flex-1 border border-green-300 rounded-lg px-2 py-1 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-400 ${!isEditable ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
                                                                    />
                                                                    {bundle?.tireCost && bundle?.tireQuantity > 1 && (
                                                                        <span className='text-xs text-gray-500 ml-2 whitespace-nowrap'>
                                                                            (Total: ${formatNumberToUs((bundle.tireCost || 0) * (bundle.tireQuantity || 1))})
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className='mb-2'>
                                                            <span className='text-xs text-green-700 font-medium'>Notes</span>
                                                            {isEditable ? (
                                                                <textarea
                                                                    placeholder='Add tire notes here...'
                                                                    value={bundle?.tireNotes || ''}
                                                                    onChange={(e) => handleBundleMetadataChange(index, 'tireNotes', e.target.value)}
                                                                    rows={2}
                                                                    className='w-full mt-1 border border-green-300 rounded-lg px-2 py-1 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-400 resize-none'
                                                                />
                                                            ) : (
                                                                <p className='text-sm text-gray-700'>{bundle?.tireNotes || '—'}</p>
                                                            )}
                                                        </div>

                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : null}

                            {workOrder?.materialList?.length ? (
                                <div className='border border-gray-200 rounded-xl overflow-hidden shadow-md bg-white mb-6'>
                                    <div className='flex justify-between items-center px-5 py-3 bg-purple-50 text-xs font-medium text-purple-700'>
                                        <span>Material</span>
                                        <span>Cost + Labour</span>
                                    </div>
                                    {workOrder.materialList.map((material, index) => (
                                        <div
                                            key={`material-${index}`}
                                            className={`flex justify-between items-center px-5 py-3 border-b border-gray-200 last:border-b-0 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-purple-50 transition-colors`}
                                        >
                                            <span className='text-gray-700'>{material?.name || 'Material'}</span>
                                            <span className='text-gray-800 font-medium'>${formatNumberToUs((material?.cost || 0) + (material?.labour || 0))}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : null}

                            {customList?.length ? (
                                <div className='border border-gray-200 rounded-xl overflow-hidden shadow-md bg-white mb-6'>
                                    <div className='flex justify-between items-center px-5 py-3 bg-amber-50 text-xs font-medium text-amber-700'>
                                        <span>Custom Service</span>
                                        <span>Price</span>
                                    </div>
                                    {customList.map((item, index) => (
                                        <div
                                            key={`custom-${index}`}
                                            className={`flex justify-between items-center gap-4 px-5 py-3 border-b border-gray-200 last:border-b-0 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-amber-50 transition-colors`}
                                        >
                                            <div className='flex items-center gap-2' style={{ width: '300px' }}>
                                                {editingCustomNameIndex === index ? (
                                                    <>
                                                        <input
                                                            type='text'
                                                            placeholder='Custom Service Name'
                                                            value={item?.itemName || item?.name || ''}
                                                            onChange={(e) => handleCustomNameChange(index, e.target.value)}
                                                            autoFocus
                                                            className='flex-1 border border-amber-300 rounded-lg px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400'
                                                        />
                                                        <FiCheck
                                                            className='text-green-600 cursor-pointer hover:text-green-700 flex-shrink-0'
                                                            onClick={() => setEditingCustomNameIndex(null)}
                                                        />
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className='flex-1 truncate text-gray-700'>{item?.itemName || item?.name || 'Custom Service'}</span>
                                                        {isEditable && (
                                                            <FiEdit
                                                                className='text-amber-600 cursor-pointer hover:text-amber-700 flex-shrink-0'
                                                                onClick={() => setEditingCustomNameIndex(index)}
                                                            />
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                            <div className='flex items-center gap-1'>
                                                <span className='text-gray-500 text-sm'>$</span>
                                                <input
                                                    type='number'
                                                    step='0.01'
                                                    min='0'
                                                    placeholder='0.00'
                                                    value={item?.price ?? item?.cost ?? ''}
                                                    onChange={(e) => handleCustomPriceChange(index, e.target.value)}
                                                    onKeyDown={(e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
                                                    disabled={!isEditable}
                                                    className={`w-24 border border-gray-300 rounded-lg px-2 py-1 text-sm text-right text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-amber-400 ${!isEditable ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : null}

                            {tireList?.length ? (
                                <div className='border border-gray-200 rounded-xl overflow-hidden shadow-md bg-white mb-6'>
                                    <div className='grid grid-cols-[2fr_1fr_1fr_auto] gap-4 items-center px-5 py-3 bg-indigo-50 text-xs font-medium text-indigo-700'>
                                        <span>Tire</span>
                                        <span>Quantity</span>
                                        <span className='text-right'>Price</span>
                                        <span className='text-center'>Tax</span>
                                    </div>
                                    {tireList.map((tire, index) => (
                                        <div
                                            key={`tire-${index}`}
                                            className={`px-5 py-3 border-b border-gray-200 last:border-b-0 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-indigo-50 transition-colors`}
                                        >
                                            <div className='grid grid-cols-[2fr_1fr_1fr_auto] gap-4 items-center mb-2'>
                                                <span className='text-gray-700'>{tire?.name || 'Tire'}</span>
                                                <select
                                                    value={tire?.quantity || 1}
                                                    onChange={(e) => handleTireQuantityChange(index, e.target.value)}
                                                    disabled={!isEditable}
                                                    className={`border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-400 ${!isEditable ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
                                                >
                                                    <option value="1">1</option>
                                                    <option value="2">2</option>
                                                    <option value="3">3</option>
                                                    <option value="4">4</option>
                                                </select>
                                                <div className='flex items-center gap-1 justify-end'>
                                                    <span className='text-gray-500'>$</span>
                                                    <input
                                                        type='number'
                                                        step='0.01'
                                                        min='0'
                                                        placeholder='0.00'
                                                        value={tire?.cost ?? ''}
                                                        onChange={(e) => handleTirePriceChange(index, e.target.value)}
                                                        onKeyDown={(e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
                                                        disabled={!isEditable}
                                                        className={`w-24 border border-gray-300 rounded-lg px-2 py-1 text-right focus:outline-none focus:ring-2 focus:ring-indigo-400 ${!isEditable ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
                                                    />
                                                </div>
                                                <div className='flex justify-center'>
                                                    <input
                                                        type='checkbox'
                                                        checked={tire?.includeTax || false}
                                                        onChange={(e) => handleTireTaxToggle(index, e.target.checked)}
                                                        disabled={!isEditable}
                                                        className={`w-4 h-4 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-400 ${!isEditable ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className='text-xs text-gray-600 mb-1 block'>Notes</label>
                                                <textarea
                                                    placeholder='Add notes here...'
                                                    value={tire?.notes || ''}
                                                    onChange={(e) => handleTireNotesChange(index, e.target.value)}
                                                    disabled={!isEditable}
                                                    rows={2}
                                                    className={`w-full border border-gray-300 rounded-lg px-2 py-1 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none ${!isEditable ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : null}

                            {workOrder?.notes && (
                                <div className='border border-gray-200 rounded-xl overflow-hidden shadow-md bg-white mb-6'>
                                    <div
                                        className='flex justify-between items-center px-5 py-3 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors'
                                        onClick={() => setIsNotesOpen(!isNotesOpen)}
                                    >
                                        <span className='text-sm font-medium text-gray-700'>Additional Notes</span>
                                        {isNotesOpen ? (
                                            <FiChevronUp className='text-gray-600' />
                                        ) : (
                                            <FiChevronDown className='text-gray-600' />
                                        )}
                                    </div>
                                    {isNotesOpen && (
                                        <div className='px-5 py-4 border-t border-gray-200'>
                                            <p className='text-sm text-gray-700 whitespace-pre-wrap'>{workOrder.notes}</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className='border border-gray-200 rounded-xl overflow-hidden shadow-md bg-white'>
                                <div className='flex justify-between items-center px-5 py-3 border-b border-gray-200'>
                                    <span className='text-sm text-gray-600'>Subtotal</span>
                                    <span className='text-sm text-gray-800 font-medium'>${formatNumberToUs(calculatedSubtotal)}</span>
                                </div>
                                <div className='flex justify-between items-center px-5 py-3 border-b border-gray-200'>
                                    <span className='text-sm text-gray-600'>Tax ({workOrder?.taxRatePercent || 0}%)</span>
                                    <span className='text-sm text-gray-800 font-medium'>${formatNumberToUs(calculatedTax)}</span>
                                </div>
                                <div className='flex justify-between items-center px-5 py-4 bg-blue-50 text-blue-800'>
                                    <span>Total Amount</span>
                                    <span>${formatNumberToUs(calculatedTotal)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            )
            }
            <Table
                options={options}
                url={url + "/fc_service_req"}
                pagination={true}
                // clickHandler={() => setShowAddFleet(!showAddFleet)}
                btnTitle={"Fleet Customer"}
                excludeFilter={excludeFilter}
                reloadTable={reloadTable}
                noButton={true}
                setViewData={setViewData}
            />
        </div >
    )
}

export default FcQuote;